import React from 'react';
import { isOverdue, fmtDate, avInitials, today } from '../lib/utils';

const PRIORITY_STYLES = {
  P0: { background:'#FEE2E2', color:'#991B1B' },
  P1: { background:'#FEF3C7', color:'#92400E' },
  P2: { background:'#DBEAFE', color:'#1E40AF' },
  P3: { background:'#F1F5F9', color:'#7A8BA3' },
};

const AV_GRADIENTS = {
  arshad:  'linear-gradient(135deg,#0EA5E9,#0369A1)',
  fahad:   'linear-gradient(135deg,#6366F1,#3730A3)',
  prateek: 'linear-gradient(135deg,#F59E0B,#B45309)',
  utkarsh: 'linear-gradient(135deg,#EC4899,#9D174D)',
  zeba:    'linear-gradient(135deg,#A855F7,#6B21A8)',
  azmath:  'linear-gradient(135deg,#10B981,#065F46)',
};

export default function TaskRow({ task, onEdit, onCycle, onDelete }) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const overdue = isOverdue(task);
  const status  = overdue ? 'overdue' : task.status;

  const daysLeft = task.deadline
    ? Math.ceil((new Date(task.deadline) - today) / 86400000)
    : null;

  let deadlineLabel = fmtDate(task.deadline);
  let deadlineCls   = '';
  let tinyLabel     = '';

  if (task.deadline) {
    if (overdue) {
      deadlineCls = 'overdue';
      tinyLabel   = `${Math.abs(daysLeft)}d overdue`;
    } else if (daysLeft !== null && daysLeft <= 2) {
      deadlineCls = 'soon';
      tinyLabel   = daysLeft === 0 ? 'today' : `${daysLeft}d left`;
    } else if (daysLeft !== null) {
      tinyLabel = `${daysLeft}d left`;
    }
  } else if (task.status === 'in_progress') {
    deadlineLabel = '⚠ none set';
    deadlineCls   = 'overdue';
  }

  const statusStyle = STATUS_PILL_STYLES[status] || STATUS_PILL_STYLES.pending;
  const avGrad      = AV_GRADIENTS[task.owner?.toLowerCase()] || 'linear-gradient(135deg,#94A3B8,#475569)';
  const rowStyle = isMobile ? { ...styles.row, gridTemplateColumns:'34px 1fr 30px' } : styles.row;

  const rowBg = overdue
    ? 'linear-gradient(90deg,rgba(239,68,68,.04),transparent 60%)'
    : 'transparent';

  return (
    <div
      onClick={() => onEdit(task.id)}
      style={{ ...rowStyle, background: rowBg }}
      onMouseEnter={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(10,37,64,.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = rowBg; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Check */}
      <button
        onClick={e => { e.stopPropagation(); onCycle(task.id); }}
        style={{
          ...styles.check,
          background: task.status === 'done' ? '#10B981' : task.status === 'in_progress' ? '#F59E0B' : 'white',
          borderColor: task.status === 'done' ? '#10B981' : task.status === 'in_progress' ? '#F59E0B' : '#E4ECF4',
        }}
        aria-label="Cycle status"
      >
        {task.status === 'done' && (
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" width="11" height="11">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )}
        {task.status === 'in_progress' && (
          <span style={{ width:8, height:8, borderRadius:'50%', background:'white', display:'block' }} />
        )}
      </button>

      {/* Title + tags + meta (all in one on mobile) */}
      <div style={styles.main}>
        <div style={{ ...styles.title, color: task.status==='done' ? '#7A8BA3' : '#0A2540', textDecoration: task.status==='done' ? 'line-through' : 'none' }}>
          {task.title}
        </div>
        <div style={styles.sub}>
          {task.link && (
            <span
              style={{ ...styles.tag, ...styles.tagLink }}
              onClick={e => { e.stopPropagation(); window.open(task.link,'_blank'); }}
            >🔗 linked</span>
          )}
          {overdue && task.eta && (
            <span style={{ ...styles.tag, ...styles.tagWarn }}>new ETA {fmtDate(task.eta)}</span>
          )}
          {overdue && task.reason && (
            <span style={styles.reasonSnippet}>"{task.reason.slice(0,60)}{task.reason.length>60?'…':''}"</span>
          )}
          {task.status==='in_progress' && !task.deadline && (
            <span style={{ ...styles.tag, ...styles.tagDanger }}>deadline missing</span>
          )}
        </div>
        {isMobile && (
          <div style={{ ...styles.mobileMeta, marginTop:6, paddingTop:6, borderTop:'1px solid #E4ECF4', display:'flex', gap:8, flexWrap:'wrap', fontSize:11 }}>
            <span style={{ ...styles.priority, ...PRIORITY_STYLES[task.priority] }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:'currentColor', display:'inline-block', marginRight:4 }} />
              {task.priority}
            </span>
            <span style={{ ...styles.owner, gap:4 }}>
              <span style={{ ...styles.av, width:18, height:18, fontSize:8, background: avGrad }}>{avInitials(task.owner)}</span>
            </span>
            <span style={{ color: deadlineCls==='overdue' ? '#EF4444' : deadlineCls==='soon' ? '#B45309' : '#3D5878' }}>
              {deadlineLabel}
            </span>
            <span style={{ ...styles.statusPill, ...statusStyle, padding:'2px 8px', fontSize:10 }}>
              <span style={{ width:4, height:4, borderRadius:'50%', background:'currentColor', display:'inline-block', marginRight:4 }} />
              {STATUS_LABELS[status]}
            </span>
          </div>
        )}
      </div>

      {!isMobile && (
        <>
          {/* Priority (desktop only) */}
          <div>
            <span style={{ ...styles.priority, ...PRIORITY_STYLES[task.priority] }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:'currentColor', display:'inline-block', marginRight:6 }} />
              {task.priority}
            </span>
          </div>

          {/* Owner (desktop only) */}
          <div style={styles.owner}>
            <span style={{ ...styles.av, background: avGrad }}>{avInitials(task.owner)}</span>
            <span style={{ fontSize:12 }}>{task.owner}</span>
          </div>

          {/* Deadline (desktop only) */}
          <div style={{ ...styles.deadline, color: deadlineCls==='overdue' ? '#EF4444' : deadlineCls==='soon' ? '#B45309' : '#3D5878', fontWeight: deadlineCls ? 600 : 400 }}>
            {deadlineLabel}
            {tinyLabel && <span style={{ display:'block', fontSize:9.5, color:'#7A8BA3', marginTop:2, fontWeight:400, textTransform:'uppercase', letterSpacing:'0.04em' }}>{tinyLabel}</span>}
          </div>

          {/* Status (desktop only) */}
          <div>
            <span style={{ ...styles.statusPill, ...statusStyle }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:'currentColor', display:'inline-block', marginRight:5 }} />
              {STATUS_LABELS[status]}
            </span>
          </div>
        </>
      )}

      {/* Delete */}
      <button
        onClick={e => { e.stopPropagation(); onDelete(task.id); }}
        style={styles.menuBtn}
        aria-label="Delete"
        onMouseEnter={e => { e.currentTarget.style.background='#F1F5F9'; e.currentTarget.style.color='#0A2540'; }}
        onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#7A8BA3'; }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="14" height="14">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6"/>
        </svg>
      </button>
    </div>
  );
}

const STATUS_LABELS  = { pending:'Pending', in_progress:'In progress', done:'Done', overdue:'Overdue' };
const STATUS_PILL_STYLES = {
  pending:     { background:'#F1F5F9', color:'#3D5878' },
  in_progress: { background:'#FEF3C7', color:'#92400E' },
  done:        { background:'#DCFCE7', color:'#065F46' },
  overdue:     { background:'#FEE2E2', color:'#991B1B' },
};

const styles = {
  row: {
    display:'grid',
    gridTemplateColumns:'34px minmax(0,1fr) 110px 120px 110px 130px 30px',
    alignItems:'center', gap:14, padding:'11px 14px', borderRadius:11,
    transition:'all .15s ease', cursor:'pointer',
  },
  check: {
    width:22, height:22, borderRadius:'50%', border:'2px solid', cursor:'pointer',
    display:'grid', placeItems:'center', transition:'all .2s ease', flexShrink:0,
  },
  main: { minWidth:0 },
  title: { fontSize:13.5, fontWeight:500, letterSpacing:'-0.005em', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  sub: { display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:3 },
  tag: { display:'inline-flex', alignItems:'center', gap:4, padding:'1px 7px', borderRadius:99, fontSize:10.5 },
  tagLink: { background:'#E6F4FB', color:'#0284C7', cursor:'pointer' },
  tagWarn: { background:'#FEF3C7', color:'#B45309' },
  tagDanger: { background:'#FEE2E2', color:'#B91C1C', fontWeight:500 },
  reasonSnippet: { fontStyle:'italic', color:'#3D5878', fontSize:11.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:280 },
  priority: { display:'inline-flex', alignItems:'center', fontSize:10.5, fontWeight:600, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'0.04em', padding:'3px 9px', borderRadius:6 },
  owner: { display:'flex', alignItems:'center', gap:7 },
  av: { width:22, height:22, borderRadius:'50%', display:'grid', placeItems:'center', color:'white', fontSize:10, fontWeight:600, flexShrink:0 },
  deadline: { fontSize:12, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'-0.02em' },
  statusPill: { display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:99, fontSize:11, fontWeight:500 },
  menuBtn: { width:26, height:26, display:'grid', placeItems:'center', borderRadius:6, color:'#7A8BA3', background:'transparent', border:'none', cursor:'pointer', transition:'all .15s ease' },
};
